import { Box, Typography } from '@mui/material';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { LeadStatusResponse } from '@layouts/pages/Settings/general/lead-status/services/responses/lead-status.response';
import { BoardDetailResponse } from '../../services/responses/board.response';
import { getCrmColors } from '../../crm-theme';
import { LeadFilters, countVisibleByStatus, countVisibleLeads } from '../../filters/crm-lead-filters';
import { statusMetricSx } from './crm-board-metrics.styles';

interface Props {
    board: BoardDetailResponse;
    statuses: LeadStatusResponse[];
    filters: LeadFilters;
    hasFilters: boolean;
    statusFilterId: string | null;
    onStatusFilterChange: (statusId: string | null) => void;
}

/// Total de leads e contagem por status; clicar num status filtra por ele.
export const CrmBoardMetrics = ({ board, statuses, filters, hasFilters, statusFilterId, onStatusFilterChange }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const total = board.metrics?.totalLeads ?? 0;

    // Com filtro, os totais acompanham os cards: o do servidor conta o quadro inteiro.
    const visibleByStatus = hasFilters ? countVisibleByStatus(board.columns, filters) : null;
    const countOf = (statusId: string) => (visibleByStatus
        ? visibleByStatus.get(statusId) ?? 0
        : board.metrics?.byStatus.find((s) => s.statusId === statusId)?.count ?? 0);

    /// Clicar no status já filtrado desliga o filtro.
    const toggleStatus = (statusId: string) => onStatusFilterChange(statusFilterId === statusId ? null : statusId);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', rowGap: 0.75 }}>
            {/* Com filtro, "X de Y leads"; sem, só o total do quadro. */}
            <Typography variant="caption" fontWeight={700} sx={{ color: cc.textPrimary }}>
                <strong>{hasFilters ? countVisibleLeads(board.columns, filters) : total}</strong>{' '}
                {hasFilters ? t('crmKanban.deTotalLeads', { count: total }) : t('crmKanban.leads', { count: total })}
            </Typography>

            {statuses.map((status) => (
                <Box key={status.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: '1px', height: '12px', bgcolor: cc.dividerStrong }} />
                    <Box
                        role="button"
                        tabIndex={0}
                        aria-pressed={statusFilterId === status.id}
                        onClick={() => toggleStatus(status.id)}
                        onKeyDown={(e) => {
                            if (e.key !== 'Enter' && e.key !== ' ') return;
                            e.preventDefault();
                            toggleStatus(status.id);
                        }}
                        sx={statusMetricSx(cc, status.color, statusFilterId === status.id)}
                    >
                        <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ color: statusFilterId === status.id ? cc.onTint : cc.textPrimary, userSelect: 'none' }}
                        >
                            <strong style={{ color: status.color }}>{countOf(status.id)}</strong> {status.name}
                        </Typography>
                    </Box>
                </Box>
            ))}
        </Box>
    );
};
